<template>
    <div class="layout-topbar">
        <button class="p-link layout-menu-button layout-topbar-button" @click="onMenuToggle">
            <i class="pi pi-bars" />
        </button>
        <div class="topbar-right-menu">
            <div :class="topbarMenuClasses">
                <NavbarThemeSwitcher />
            </div>
            <NavBarI18n />
            <div v-if="userData && userData.name" class="flex items-center ml-2 text-xs sm:text-sm">
                {{ userData.name }}
            </div>
            <button class="ml-2 cursor-pointer" aria-haspopup="true" aria-controls="user_menu" @click="openUserMenu">
                <Avatar
                    :image="userData.image ? `/api/management/v1/attachments/250_${userData.image}` : undefined"
                    :label="userData.image ? '' : getNameInitials(userData.name)"
                    class="circle-avatar !size-9 lg:!size-10"
                    size="normal"
                    shape="circle"
                />
            </button>
            <Menu
                id="user_menu"
                ref="userMenu"
                :pt="{ itemLink: '!text-[initial]' }"
                :model="userMenuItems"
                :popup="true"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Avatar } from 'primevue'
    import Menu from 'primevue/menu'
    import { type Ref, computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRouter } from 'vue-router'

    import { useLayout } from '../layout'

    import NavBarI18n from '@/layouts/components/NavBarI18n.vue'
    import NavbarThemeSwitcher from '@/layouts/components/NavbarThemeSwitcher.vue'
    import { getNameInitials } from '@/libs/helpers.ts'
    import { useAppStore } from '@/stores/app'

    // data
    const appStore = useAppStore()
    const { onMenuToggle } = useLayout()
    const topbarMenuActive: Ref<boolean> = ref(false)
    const userMenu = useTemplateRef<InstanceType<typeof Menu>>('userMenu')
    const t = useI18n().t
    const router = useRouter()

    const logout = async() => {
        appStore.setLogout()
        document.cookie = 'JWT=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        await router.push({ name: 'login' })
    }

    const goToProfilePage = () => {
        router.push({ name: 'profile' })
    }

    const userMenuItems = [
        { label: () => { return t('general.profile') }, icon: 'pi pi-fw pi-user', command: goToProfilePage },
        { separator: true },

        {
            label: () => {
                return t('general.logout')
            },
            icon: 'pi pi-fw pi-sign-out',
            command: logout
        }
    ]

    // methods
    const openUserMenu = (event: Event) => {
        if (userMenu.value) userMenu.value.toggle(event)
    }

    const outsideClickListener = (event: Event) => {
        if (!topbarMenuActive.value) return

        const sidebarEl = document.querySelector('.layout-topbar-menu')
        const topbarEl = document.querySelector('.layout-topbar-menu-button')

        if (sidebarEl && topbarEl) {
            topbarMenuActive.value = (sidebarEl.isSameNode(event.target as HTMLElement) || sidebarEl.contains(event.target as HTMLElement) || topbarEl.isSameNode(event.target as HTMLElement) || topbarEl.contains(event.target as HTMLElement))
        } else {
            topbarMenuActive.value = false
        }
    }

    // computed
    const topbarMenuClasses = computed(() => {
        return {
            'layout-topbar-menu-mobile-active': topbarMenuActive.value
        }
    })

    const userData = computed(() => {
        return appStore.getUserData()
    })

    // hook
    onMounted(() => {
        document.addEventListener('click', outsideClickListener)
    })

    onBeforeUnmount(() => {
        document.removeEventListener('click', outsideClickListener)
    })
</script>

<style scoped>
.circle-avatar {
  border-radius: 50%;
  overflow: hidden;
  background-size: cover;
}
</style>
